import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';

const PaymentModal = ({
    isOpen,
    onClose,
    studentData,
    balance = 0,
    initialAmount = null,
    initialAccountRef = '',
    modalTitle = '',
    subtitle = '',
    isActivityPayment = false,
    onPaymentSuccess
}) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [checkoutRequestId, setCheckoutRequestId] = useState(null);
    const [pollingStatus, setPollingStatus] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState(''); // Would typically prefill from user profile
    const [accountReference, setAccountReference] = useState('');
    const effectiveBalance = initialAmount !== null ? initialAmount : balance;
    const [amountType, setAmountType] = useState(effectiveBalance <= 0 ? 'custom' : 'full'); // 'full' or 'custom'
    const [customAmount, setCustomAmount] = useState('');

    useEffect(() => {
        if (isOpen) {
            setAccountReference(initialAccountRef || studentData?.student_id || '');
            if (initialAmount !== null && initialAmount > 0) {
                setAmountType('full');
            }
        }
    }, [isOpen, initialAccountRef, initialAmount, studentData]);
    
    // Swipe down to close logic
    const [dragY, setDragY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [touchStart, setTouchStart] = useState(null);
    const minSwipeDistance = 100;

    const onTouchStart = (e) => {
        setTouchStart(e.targetTouches[0].clientY);
        setIsDragging(true);
    };

    const onTouchMove = (e) => {
        if (!touchStart) return;
        const currentY = e.targetTouches[0].clientY;
        const diff = currentY - touchStart;
        if (diff > 0) {
            setDragY(diff);
        }
    };

    const onTouchEndHandler = () => {
        setIsDragging(false);
        if (dragY > minSwipeDistance) {
            onClose();
            setTimeout(() => setDragY(0), 300);
        } else {
            setDragY(0);
        }
        setTouchStart(null);
    };

    const { post, get } = useApi();

    const handlePayment = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Calculate final amount
        const amountToPay = amountType === 'full' ? effectiveBalance : parseFloat(customAmount);

        try {
            const response = await post('/api/finance/mpesa/stk-push/', {
                student_id: studentData?.id,
                phone: phoneNumber,
                amount: amountToPay,
                account_reference: accountReference.trim()
            });

            if (response.checkout_request_id) {
                setCheckoutRequestId(response.checkout_request_id);
                setStep(2); // Success step
            }
        } catch (error) {
            console.error('M-Pesa payment failed:', error);
            // Optionally, handle error state here
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let intervalId;

        const checkStatus = async () => {
            if (!checkoutRequestId || step !== 2) return;

            try {
                const res = await get(`/api/finance/mpesa/status/${checkoutRequestId}/`);
                if (res.status === 'success') {
                    setPollingStatus('success');
                    clearInterval(intervalId);
                    setTimeout(() => {
                        onClose();
                        setStep(1);
                        setCheckoutRequestId(null);
                        setPollingStatus(null);
                        onPaymentSuccess?.();
                    }, 3000);
                } else if (res.status === 'failed') {
                    setPollingStatus('failed');
                    clearInterval(intervalId);
                }
            } catch (err) {
                console.error("Failed to fetch status:", err);
            }
        };

        if (step === 2 && checkoutRequestId && pollingStatus !== 'success' && pollingStatus !== 'failed') {
            intervalId = setInterval(checkStatus, 4000);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [step, checkoutRequestId, pollingStatus, get, onClose, onPaymentSuccess]);

    // Prevent background scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(amount);
    };

    return (
        <div className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center p-0 sm:p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            ></div>

            {/* Modal Content / Bottom Sheet */}
            <div 
                className={`relative bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] w-full max-w-[500px] shadow-2xl overflow-hidden sm:scale-100 max-h-[90vh] overflow-y-auto custom-scrollbar ${dragY === 0 && !isDragging ? 'animate-in slide-in-from-bottom-full sm:slide-in-from-bottom-4 duration-300' : ''}`}
                style={{ 
                    transform: `translateY(${dragY}px)`, 
                    transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)' 
                }}
            >
                {/* Mobile drag handle */}
                <div 
                    className="w-full flex justify-center pt-4 pb-6 sm:hidden absolute top-0 left-0 right-0 z-50 bg-white cursor-pointer touch-none"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEndHandler}
                >
                    <div className="w-12 h-1.5 bg-slate-200 rounded-full"></div>
                </div>

                {step === 1 ? (
                    <div className="p-6 pt-10 sm:p-8">
                        <div className="flex justify-between items-center mb-8">
                            <div>
                                <h3 className="text-2xl font-black text-[#18216D]">
                                    {modalTitle || 'Make Payment'}
                                </h3>
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                                    {subtitle || (studentData?.first_name ? `For ${studentData.first_name}` : 'School Payment')}
                                    {studentData?.student_id ? ` • Adm ${studentData.student_id}` : ''}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="h-10 w-10 rounded-full bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 flex items-center justify-center transition-colors"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <form onSubmit={handlePayment} className="space-y-6">
                            {/* Phone Input */}
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                                    M-Pesa Phone Number *
                                </label>
                                <div className="relative">
                                    <input
                                        type="tel"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        placeholder="07... or 2547..."
                                        className="w-full bg-white border border-slate-300 shadow-sm rounded-2xl px-5 py-4 pl-12 text-[#18216D] font-bold focus:ring-4 focus:ring-indigo-50 focus:border-indigo-400 transition-all placeholder:text-slate-400 text-sm"
                                        required
                                    />
                                    <i className="fas fa-mobile-alt absolute left-5 top-1/2 -translate-y-1/2 text-slate-400"></i>
                                </div>
                            </div>

                            {/* Account Reference Input (Editable by User) */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                        Account Reference (Paybill Account) *
                                    </label>
                                    <span className={`text-[10px] font-bold ${accountReference.length > 12 ? 'text-rose-500 font-black' : 'text-slate-400'}`}>
                                        {accountReference.length}/12 chars
                                    </span>
                                </div>
                                <div className="relative">
                                    <input
                                        type="text"
                                        maxLength={12}
                                        value={accountReference}
                                        onChange={(e) => setAccountReference(e.target.value.toUpperCase())}
                                        placeholder={studentData?.student_id ? `${studentData.student_id} ACTIVITY` : "E.g. 4734 ROBOTICS"}
                                        className="w-full bg-white border border-slate-300 shadow-sm rounded-2xl px-5 py-4 pl-12 text-[#18216D] font-bold uppercase tracking-wider focus:ring-4 focus:ring-indigo-50 focus:border-indigo-400 transition-all placeholder:text-slate-300 text-xs sm:text-sm"
                                        required
                                    />
                                    <i className="fas fa-tag absolute left-5 top-1/2 -translate-y-1/2 text-slate-400"></i>
                                </div>
                                <p className="text-[10px] text-slate-400 mt-1.5 leading-tight">
                                    Usually student admission no + activity code. M-Pesa limits references to 12 characters.
                                </p>
                            </div>

                            {/* Amount Selection */}
                            {isActivityPayment && initialAmount !== null ? (
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                                        Activity Entry Fee
                                    </label>
                                    <div className="bg-indigo-50/40 border border-indigo-100 rounded-2xl p-4 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-xl bg-[#18216D]/10 flex items-center justify-center text-[#18216D]">
                                                <i className="fas fa-ticket text-sm"></i>
                                            </div>
                                            <div>
                                                <span className="text-xs font-bold text-[#18216D] block">School Activity Ticket</span>
                                                <span className="text-[10px] text-slate-400">Official Entry Fee</span>
                                            </div>
                                        </div>
                                        <span className="text-lg font-black text-[#18216D]">
                                            {formatCurrency(initialAmount)}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">
                                        Amount to Pay
                                    </label>
                                    <div className="space-y-3">
                                        {/* Full Balance Option */}
                                        {balance > 0 && (
                                            <div
                                                onClick={() => setAmountType('full')}
                                                className={`cursor-pointer p-4 rounded-2xl border-2 transition-all flex items-center justify-between ${amountType === 'full'
                                                        ? 'border-[#18216D] bg-indigo-50/30'
                                                        : 'border-slate-100 hover:border-slate-200'
                                                    }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${amountType === 'full' ? 'border-[#18216D]' : 'border-slate-300'
                                                        }`}>
                                                        {amountType === 'full' && <div className="w-2.5 h-2.5 rounded-full bg-[#18216D]"></div>}
                                                    </div>
                                                    <span className={`text-sm font-bold ${amountType === 'full' ? 'text-[#18216D]' : 'text-slate-500'}`}>
                                                        Total Outstanding
                                                    </span>
                                                </div>
                                                <span className={`font-black ${amountType === 'full' ? 'text-[#18216D]' : 'text-slate-400'}`}>
                                                    {formatCurrency(balance)}
                                                </span>
                                            </div>
                                        )}

                                        {/* Custom Amount Option */}
                                        <div
                                            onClick={() => setAmountType('custom')}
                                            className={`cursor-pointer p-4 rounded-2xl border-2 transition-all ${amountType === 'custom'
                                                    ? 'border-[#18216D] bg-indigo-50/30'
                                                    : 'border-slate-100 hover:border-slate-200'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${amountType === 'custom' ? 'border-[#18216D]' : 'border-slate-300'
                                                    }`}>
                                                    {amountType === 'custom' && <div className="w-2.5 h-2.5 rounded-full bg-[#18216D]"></div>}
                                                </div>
                                                <span className={`text-sm font-bold ${amountType === 'custom' ? 'text-[#18216D]' : 'text-slate-500'}`}>
                                                    Other Amount
                                                </span>
                                            </div>

                                            {amountType === 'custom' && (
                                                <div className="relative animate-in slide-in-from-top-2 fade-in">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        value={customAmount}
                                                        onChange={(e) => {
                                                            const val = Math.max(0, parseFloat(e.target.value) || 0);
                                                            setCustomAmount(e.target.value === '' ? '' : val);
                                                        }}
                                                        placeholder="Enter amount"
                                                        className="w-full bg-white border border-[#18216D]/20 rounded-xl px-4 py-3 text-[#18216D] font-black focus:ring-0"
                                                        autoFocus
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading || !accountReference.trim() || (amountType === 'custom' && !customAmount)}
                                className="w-full py-5 bg-[#18216D] text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-[#18216D]/90 active:scale-95 transition-all shadow-xl shadow-indigo-900/20 flex items-center justify-center gap-2 disabled:opacity-50 disabled:scale-100"
                            >
                                {loading ? (
                                    <>
                                        <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-lock"></i>
                                        Pay Securely
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                ) : (
                    // Success State
                    <div className="p-12 flex flex-col items-center text-center">
                        {pollingStatus === 'success' ? (
                            <>
                                <div className="h-20 w-20 bg-emerald-100 rounded-full flex items-center justify-center mb-6 text-emerald-600 animate-bounce">
                                    <i className="fas fa-check text-4xl"></i>
                                </div>
                                <h3 className="text-2xl font-black text-[#18216D] mb-2">Payment Successful!</h3>
                                <p className="text-slate-400 font-medium text-sm">
                                    Your M-Pesa transaction was completed.
                                </p>
                            </>
                        ) : pollingStatus === 'failed' ? (
                            <>
                                <div className="h-20 w-20 bg-red-100 rounded-full flex items-center justify-center mb-6 text-red-600">
                                    <i className="fas fa-times text-4xl"></i>
                                </div>
                                <h3 className="text-2xl font-black text-[#18216D] mb-2">Payment Failed</h3>
                                <p className="text-slate-400 font-medium text-sm mb-6">
                                    The transaction was cancelled or failed. Please try again.
                                </p>
                                <button
                                    onClick={() => {
                                        setStep(1);
                                        setPollingStatus(null);
                                        setCheckoutRequestId(null);
                                    }}
                                    className="px-6 py-3 bg-red-50 text-red-600 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-red-100 transition-colors"
                                >
                                    <i className="fas fa-redo mr-2"></i>
                                    Try Again
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="h-20 w-20 border-4 border-indigo-100 border-t-[#18216D] rounded-full flex items-center justify-center mb-6 text-[#18216D] animate-spin">
                                    <i className="fas fa-mobile-alt text-2xl"></i>
                                </div>
                                <h3 className="text-2xl font-black text-[#18216D] mb-2">Processing Payment...</h3>
                                <p className="text-slate-400 font-medium text-sm">
                                    Please check your phone and enter your M-Pesa PIN. Waiting for confirmation...
                                </p>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentModal;
